import { supabase } from "../lib/supabase";
import type { SearchParams, SortParams } from "../components/Manager/types";

export type Store = {
  id: number;
  name: string;
  reference_number: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  latitude: number | null;
  longitude: number | null;
  banner_id: number;
  banners: { name: string } | null;
  created_at: string;
  updated_at: string;
};

export type FetchResult<T> = {
  data: T[];
  hasMore: boolean;
  totalCount?: number;
};

// Supabase query builder - any is needed for method chaining compatibility
type QueryBuilder = any;

type BinaryFilterHandler = (
  query: QueryBuilder,
  value: "yes" | "no",
) => QueryBuilder;

export type BinaryFilters = Record<string, BinaryFilterHandler>;

// Built-in binary filter for hasCoordinates
export const storesBinaryFilters: BinaryFilters = {
  hasCoordinates: (query, value) => {
    if (value === "yes") {
      return query.not("latitude", "is", null).not("longitude", "is", null);
    } else {
      return query.or("latitude.is.null,longitude.is.null");
    }
  },
};

export async function fetchStores(
  limit?: number,
  offset?: number,
  searchParams?: SearchParams,
  searchColumns?: string[],
  binaryFilters?: BinaryFilters,
  sortParams?: SortParams,
): Promise<FetchResult<Store>> {
  // Helper to apply all filters to a query
  const applyFilters = (q: QueryBuilder): QueryBuilder => {
    let result = q;

    // Apply text search filter
    if (searchParams?.text && searchColumns && searchColumns.length > 0) {
      const searchText = `%${searchParams.text}%`;
      const orConditions = searchColumns
        .map((col) => `${col}.ilike.${searchText}`)
        .join(",");
      result = result.or(orConditions);
    }

    // Apply binary filters
    if (searchParams?.binary && binaryFilters) {
      for (const [key, value] of Object.entries(searchParams.binary)) {
        if (value && binaryFilters[key]) {
          result = binaryFilters[key](result, value);
        }
      }
    }

    // Apply custom filters (like territory_id)
    if (searchParams?.filters) {
      for (const [key, value] of Object.entries(searchParams.filters)) {
        if (value !== undefined && value !== null) {
          result = result.eq(key, value);
        }
      }
    }

    return result;
  };

  // First, get total count with search filters
  let countQuery = supabase
    .from("stores")
    .select("*", { count: "exact", head: true });
  countQuery = applyFilters(countQuery);
  const { count } = await countQuery;

  // Now get the data with banner name join
  let query = supabase.from("stores").select(`
    *,
    banners (name)
  `);

  // Apply sorting (default to name ascending if not specified)
  const sortColumn = sortParams?.column ?? "name";
  const sortAscending = sortParams?.order !== "desc";
  query = query.order(sortColumn, { ascending: sortAscending });

  query = applyFilters(query);

  if (limit) {
    query = query.limit(limit + 1);
  }

  if (offset) {
    query = query.range(offset, offset + (limit ?? 10));
  }

  const { error, data } = await query;

  if (error) throw error;

  const items = (data ?? []) as Store[];
  const hasMore = limit ? items.length > limit : false;
  const limitedData = limit ? items.slice(0, limit) : items;

  return { data: limitedData, hasMore, totalCount: count ?? undefined };
}

/**
 * Search stores by name
 */
export async function searchStores(
  searchText: string,
  limit: number = 10,
): Promise<Store[]> {
  const { data, error } = await supabase
    .from("stores")
    .select(
      `
      *,
      banners (name)
    `,
    )
    .ilike("name", `%${searchText}%`)
    .limit(limit);

  if (error) throw error;

  return (data ?? []) as Store[];
}

/**
 * Search stores by reference number or name
 */
export async function searchStoresByNumberOrName(
  searchText: string,
  limit: number = 10,
): Promise<Store[]> {
  if (!searchText.trim()) {
    return [];
  }

  const trimmed = searchText.trim();

  const selectQuery = `
    *,
    banners (name)
  `;

  // Search by reference_number prefix OR name
  const { data, error } = await supabase
    .from("stores")
    .select(selectQuery)
    .or(`reference_number.ilike.${trimmed}%,name.ilike.%${trimmed}%`)
    .limit(limit);

  if (error) throw error;
  return (data ?? []) as Store[];
}

/**
 * Fetch a single store by ID
 */
export async function fetchStoreById(id: number): Promise<Store | null> {
  const { data, error } = await supabase
    .from("stores")
    .select(
      `
      *,
      banners (name)
    `,
    )
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }

  return data as Store;
}

/**
 * Fetch multiple stores by IDs
 */
export async function fetchStoresByIds(ids: number[]): Promise<Store[]> {
  if (ids.length === 0) return [];

  const { data, error } = await supabase
    .from("stores")
    .select(
      `
      *,
      banners (name)
    `,
    )
    .in("id", ids);

  if (error) throw error;

  return (data ?? []) as Store[];
}
